import { LitElement, html, css } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { settingsStyles } from '../../styles/settings-styles.js';
import { iconStyles } from '../../styles/icon-styles.js';
import { settingsService } from '../../services/settings-service.js';
import { authService } from '../../services/auth-service.js';
import { tz } from '../../services/timezone-service.js';

export class GeneralSettings extends LitElement {
  static properties = {
    _timezone: { state: true },
    _dateFormat: { state: true },
    _language: { state: true },
    _features: { type: Object, state: true },
    _saving: { state: true },
    _loaded: { state: true }
  };

  static styles = [designSystem, componentStyles, settingsStyles];

  constructor() {
    super();
    this._timezone = 'America/Chicago';
    this._dateFormat = 'MM/DD/YYYY';
    this._language = 'en-US';
    this._features = {
      callRecording: true,
      liveTranscription: true,
      sentimentAnalysis: false,
      aiSummaries: true
    };
    this._saving = false;
    this._loaded = false;
  }

  connectedCallback() {
    super.connectedCallback();
    this._load();
  }

  async _load() {
    try {
      const data = await settingsService.loadGeneralConfig();
      if (data.success && data.config) {
        const c = data.config;
        // Per-user timezone: voice_timezone_{userId}, fallback to system voice_timezone
        const user = authService.getUser();
        const userTzKey = user?.id ? `voice_timezone_${user.id}` : null;
        this._timezone = (userTzKey && c[userTzKey]) || c.voice_timezone || 'America/Chicago';
        this._dateFormat = c.voice_date_format || 'MM/DD/YYYY';
        this._language = c.voice_language || 'en-US';

        const toBool = (val, defOn) => {
          if (val === undefined || val === null || val === '') return defOn;
          return val === '1' || val === 1 || val === true;
        };
        this._features = {
          callRecording: toBool(c.voice_feature_call_recording, true),
          liveTranscription: toBool(c.voice_feature_live_transcription, true),
          sentimentAnalysis: toBool(c.voice_feature_sentiment_analysis, false),
          aiSummaries: toBool(c.voice_feature_ai_summaries, true),
        };
        this._loaded = true;
      }
    } catch (err) {
      console.error('[GeneralSettings] Load failed:', err);
    }
  }

  _toggleFeature(key) {
    this._features = { ...this._features, [key]: !this._features[key] };
  }

  async _save() {
    if (this._saving) return;
    this._saving = true;

    try {
      const user = authService.getUser();
      const userTzKey = user?.id ? `voice_timezone_${user.id}` : 'voice_timezone';

      const payload = {
        [userTzKey]: this._timezone,
        voice_date_format: this._dateFormat,
        voice_language: this._language,
        voice_feature_call_recording: this._features.callRecording ? '1' : '0',
        voice_feature_live_transcription: this._features.liveTranscription ? '1' : '0',
        voice_feature_sentiment_analysis: this._features.sentimentAnalysis ? '1' : '0',
        voice_feature_ai_summaries: this._features.aiSummaries ? '1' : '0',
      };

      const data = await settingsService.saveGeneralConfig(payload);
      if (data.success) {
        tz.setUser(this._timezone);
        this.dispatchEvent(new CustomEvent('toast', { detail: { message: data.message || 'General settings saved', type: 'success' }, bubbles: true, composed: true }));
        this.dispatchEvent(new CustomEvent('settings-saved', { bubbles: true, composed: true }));
      } else {
        this.dispatchEvent(new CustomEvent('toast', { detail: { message: data.error || 'Save failed', type: 'error' }, bubbles: true, composed: true }));
      }
    } catch (err) {
      this.dispatchEvent(new CustomEvent('toast', { detail: { message: 'Network error saving settings', type: 'error' }, bubbles: true, composed: true }));
    } finally {
      this._saving = false;
    }
  }

  render() {
    return html`
      ${iconStyles}
      <div class="content-header">
        <h2 class="content-title">General Settings</h2>
        <p class="content-description">Configure system-wide preferences including timezone, formatting, and feature toggles.</p>
      </div>

      <div class="settings-section">
        <div class="section-title-row">
          <i class="fa-sharp-duotone fa-solid fa-globe section-icon"></i>
          <span class="section-title">Regional Preferences</span>
        </div>
        <p class="section-description">Set your timezone, date format, and default language.</p>

        <div class="form-grid cols-3">
          <div class="form-group">
            <label class="form-label">Timezone</label>
            <select class="form-select" .value=${this._timezone} @change=${e => this._timezone = e.target.value}>
              <optgroup label="United States">
                <option value="America/New_York">Eastern Time (ET)</option>
                <option value="America/Chicago">Central Time (CT)</option>
                <option value="America/Denver">Mountain Time (MT)</option>
                <option value="America/Los_Angeles">Pacific Time (PT)</option>
                <option value="America/Anchorage">Alaska Time (AKT)</option>
                <option value="Pacific/Honolulu">Hawaii Time (HT)</option>
              </optgroup>
              <optgroup label="Canada">
                <option value="America/Toronto">Eastern (Toronto)</option>
                <option value="America/Winnipeg">Central (Winnipeg)</option>
                <option value="America/Edmonton">Mountain (Edmonton)</option>
                <option value="America/Vancouver">Pacific (Vancouver)</option>
                <option value="America/Halifax">Atlantic (Halifax)</option>
                <option value="America/St_Johns">Newfoundland (St. John's)</option>
              </optgroup>
              <optgroup label="Mexico / Central America">
                <option value="America/Mexico_City">Mexico City</option>
                <option value="America/Cancun">Cancun (EST)</option>
                <option value="America/Costa_Rica">Costa Rica</option>
              </optgroup>
              <optgroup label="South America">
                <option value="America/Bogota">Bogota</option>
                <option value="America/Sao_Paulo">Sao Paulo</option>
                <option value="America/Argentina/Buenos_Aires">Buenos Aires</option>
                <option value="America/Santiago">Santiago</option>
              </optgroup>
              <optgroup label="Europe">
                <option value="Europe/London">London (GMT/BST)</option>
                <option value="Europe/Paris">Paris (CET)</option>
                <option value="Europe/Berlin">Berlin (CET)</option>
                <option value="Europe/Madrid">Madrid (CET)</option>
                <option value="Europe/Rome">Rome (CET)</option>
                <option value="Europe/Amsterdam">Amsterdam (CET)</option>
                <option value="Europe/Athens">Athens (EET)</option>
                <option value="Europe/Moscow">Moscow (MSK)</option>
              </optgroup>
              <optgroup label="Asia">
                <option value="Asia/Dubai">Dubai (GST)</option>
                <option value="Asia/Kolkata">India (IST)</option>
                <option value="Asia/Bangkok">Bangkok (ICT)</option>
                <option value="Asia/Singapore">Singapore (SGT)</option>
                <option value="Asia/Manila">Philippines (PHT)</option>
                <option value="Asia/Hong_Kong">Hong Kong (HKT)</option>
                <option value="Asia/Shanghai">China (CST)</option>
                <option value="Asia/Tokyo">Tokyo (JST)</option>
                <option value="Asia/Seoul">Seoul (KST)</option>
              </optgroup>
              <optgroup label="Australia / Pacific">
                <option value="Australia/Sydney">Sydney (AEST)</option>
                <option value="Australia/Melbourne">Melbourne (AEST)</option>
                <option value="Australia/Brisbane">Brisbane (AEST, no DST)</option>
                <option value="Australia/Perth">Perth (AWST)</option>
                <option value="Pacific/Auckland">Auckland (NZST)</option>
              </optgroup>
              <optgroup label="Other">
                <option value="UTC">UTC</option>
              </optgroup>
            </select>
          </div>
          <div class="form-group">
            <label class="form-label">Date Format</label>
            <select class="form-select" .value=${this._dateFormat} @change=${e => this._dateFormat = e.target.value}>
              <option value="MM/DD/YYYY">MM/DD/YYYY</option>
              <option value="DD/MM/YYYY">DD/MM/YYYY</option>
              <option value="YYYY-MM-DD">YYYY-MM-DD</option>
            </select>
          </div>
          <div class="form-group">
            <label class="form-label">Language</label>
            <select class="form-select" .value=${this._language} @change=${e => this._language = e.target.value}>
              <option value="en-US">English (US)</option>
              <option value="en-GB">English (UK)</option>
              <option value="es-ES">Spanish</option>
              <option value="fr-FR">French</option>
            </select>
          </div>
        </div>
      </div>

      <div class="settings-section">
        <div class="section-title-row">
          <i class="fa-sharp-duotone fa-solid fa-toggle-on section-icon"></i>
          <span class="section-title">Feature Toggles</span>
        </div>
        <p class="section-description">Enable or disable system-wide features.</p>

        <div class="feature-list">
          ${this._renderFeature('callRecording', 'Call Recording', 'Automatically record all incoming and outgoing calls for quality assurance and compliance.', 'fa-circle-dot', 'var(--color-error)')}
          ${this._renderFeature('liveTranscription', 'Live Transcription', 'Generate real-time transcripts during active calls for monitoring and review.', 'fa-closed-captioning', 'var(--color-accent)')}
          ${this._renderFeature('sentimentAnalysis', 'Sentiment Analysis', 'Analyze caller sentiment in real-time to detect frustration, satisfaction, and urgency.', 'fa-face-smile', 'var(--color-warning)')}
          ${this._renderFeature('aiSummaries', 'AI Summaries', 'Generate AI-powered summaries after each call with key topics, action items, and outcomes.', 'fa-sparkles', 'var(--color-info)')}
        </div>
      </div>

      <div class="actions-bar">
        <button class="btn btn-primary" @click=${this._save} ?disabled=${this._saving}>
          ${this._saving
            ? html`<i class="fa-sharp fa-light fa-spinner-third" style="animation:spin 0.6s linear infinite;"></i> Saving...`
            : html`<i class="fa-sharp fa-light fa-floppy-disk"></i> Save Changes`
          }
        </button>
      </div>
    `;
  }

  _renderFeature(key, label, desc, icon, color) {
    return html`
      <div class="feature-item">
        <div class="feature-info">
          <div class="feature-label">
            <i class="fa-sharp fa-light ${icon}" style="margin-right:var(--space-2);color:${color};font-size:var(--text-xs);"></i>
            ${label}
          </div>
          <div class="feature-desc">${desc}</div>
        </div>
        <div
          class="toggle ${this._features[key] ? 'active' : ''}"
          @click=${() => this._toggleFeature(key)}
        ></div>
      </div>
    `;
  }
}

customElements.define('general-settings', GeneralSettings);
